"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import Link from "next/link";

import { PageChallengeOldDesignSchema } from "@/sanity/pages/PageChallengeOldDesignTemplate";

import Footer from "@/components/global/partials/Footer";
import HeroSection from "../pageComponents/challenge-old-design/Hero";
import DealSection from "../pageComponents/challenge-old-design/Deal";
import ForWhoSection from "../pageComponents/challenge-old-design/Who";
import Process from "../pageComponents/challenge-old-design/Process";
import About from "../pageComponents/challenge-old-design/About";
import StepsSection from "../pageComponents/challenge-old-design/Steps";
import FaqSection from "../pageComponents/comp-affiliate-old-design/FAQ";
import ReviewsSection from "../pageComponents/challenge-old-design/Reviews";
import LastCTASection from "../global/sections/LastCTA";
import { cn } from "@/lib/utils";
import { DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";
import { resolveLink } from "@/sanity/globals/CTA";

import bg from "@/public/home/section-bg.jpg";
import logo from "@/public/logo-black.svg";

export default function PageChallengeOldDesignJSX({ data }: { data: PageChallengeOldDesignSchema }) {
  const lang = data?.settings?.language || DEFAULT_LANGUAGE;

  const homeHref = resolveLink(lang && lang !== DEFAULT_LANGUAGE ? `/${lang}` : "/", lang);

  const [finalLink, setFinalLink] = useState("");

  useEffect(() => {
    if (!homeHref) return;
    setFinalLink(homeHref);
  }, [homeHref]);

  return (
    <main className="prose-headings:font-[600] prose-headings:tracking-[-0.03em]">
      <nav
        className={cn(
          "flex absolute z-40 justify-between items-end w-full lg:px-0 top-[4rem] start-0 px-[4rem] lg:top-[9rem]",
        )}>
        <div className="relative z-[3] mx-auto flex w-full max-w-[187.375rem] items-center justify-between">
          <div className={`w-full py-[1.5rem] lg:py-0 mx-auto flex justify-center`}>
            <Link
              className="relative z-[2] flex w-fit"
              href={finalLink}>
              <Image
                alt="Logo"
                src={logo}
                className="w-[18.375rem]"
              />
            </Link>
          </div>
        </div>
      </nav>

      <HeroSection data={data} />
      <DealSection data={data} />
      <div
        className="relative mb-[2.5rem] w-full px-[2rem] lg:mb-[5rem] lg:px-[5rem]"
        style={{ overflowAnchor: "none" }}>
        <div className="relative mx-auto w-[min(229.25rem,100%)] overflow-clip rounded-[2rem] lg:rounded-[5rem]">
          <div className="pointer-events-none absolute top-0 left-0 h-full w-full bg-[#E6EDE9]">
            <Image
              className="absolute bottom-0 left-1/2 !h-auto !w-[max(100%,120rem)] !max-w-none -translate-x-1/2 object-cover opacity-[.10] mix-blend-luminosity"
              style={{
                maskImage: "linear-gradient(to top, black 20%, transparent 100%)",
                WebkitMaskImage: "linear-gradient(to top, black 20%, transparent 100%)",
              }}
              src={bg}
              quality={100}
              alt="background art"
            />
          </div>
          <ForWhoSection data={data} />
        </div>
      </div>
      <div
        className="relative w-full px-[2rem] lg:px-[5rem]"
        style={{ overflowAnchor: "none" }}>
        <div className="relative mx-auto w-[min(229.25rem,100%)] overflow-clip rounded-[2rem] lg:rounded-[5rem]">
          <div className="pointer-events-none absolute top-0 left-0 h-full w-full bg-[#103037]">
            <Image
              className="absolute bottom-0 left-1/2 !h-auto !w-[max(100%,120rem)] !max-w-none -translate-x-1/2 object-cover opacity-[.10] mix-blend-luminosity"
              style={{
                maskImage: "linear-gradient(to top, black 20%, transparent 100%)",
                WebkitMaskImage: "linear-gradient(to top, black 20%, transparent 100%)",
              }}
              src={bg}
              quality={100}
              alt="background art"
            />
          </div>
          <Process data={data} />
        </div>
      </div>

      {data.reviewsTitle && data.reviewsDocument && (
        <ReviewsSection
          reviewsTitle={data.reviewsTitle}
          reviewsDocument={data.reviewsDocument}
          slug={data?.settings?.slug?.current}
        />
      )}

      <div
        className="relative w-full px-[2rem] lg:px-[5rem]"
        style={{ overflowAnchor: "none" }}>
        <div className="relative mx-auto w-[min(229.25rem,100%)] overflow-clip rounded-[2rem] lg:rounded-[5rem]">
          <div className="pointer-events-none absolute top-0 left-0 h-full w-full bg-[#E6EDE9]">
            <Image
              className="absolute top-0 left-1/2 !h-auto !w-[max(100%,120rem)] !max-w-none -translate-x-1/2 -scale-100 object-cover opacity-[.10] mix-blend-luminosity"
              style={{
                maskImage: "linear-gradient(to top, black 20%, transparent 100%)",
                WebkitMaskImage: "linear-gradient(to top, black 20%, transparent 100%)",
              }}
              src={bg}
              quality={100}
              alt="background art"
            />
          </div>
          <About data={data} />
          <StepsSection
            chip={data.stepsChip}
            title={data.stepsTitle}
            steps={data.steps}
            cta={data.stepsCTA}
          />
        </div>
      </div>

      <FaqSection
        chip={data.faqChip}
        title={data.faqTitle}
        faqList={data.faqList}
      />

      <LastCTASection
        chip={data?.lastChip}
        text={data?.lastText}
        cta={data?.lastCTA}
      />
      {data?.settings?.footer && <Footer data={data.settings.footer} language={data?.settings?.language} hideFinalCta />}
    </main>
  );
}
